import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  Clock,
  CreditCard,
  Mail,
  Megaphone,
  Check,
  Power,
  RotateCcw,
  Save,
  Send,
} from 'lucide-react';
import { Alert, Badge, Button, Card, CardBody, CardHeader, CardTitle, Spinner } from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import { CARD_INSTALLMENTS, grossUpInstallments } from '@pnl/shared';
import type { BannerConfig, CampaignSettingKey, OperatingWindow } from '@pnl/shared';
import { api } from '../lib/api';

export function SettingsPage() {
  const qc = useQueryClient();

  const campaignsQuery = useQuery({
    queryKey: ['admin', 'settings', 'campaigns'],
    queryFn: () => api.getCampaignSettings(),
  });

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: CampaignSettingKey; enabled: boolean }) =>
      api.setCampaignSetting({ key, enabled }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'campaigns'], data);
    },
  });

  const items = campaignsQuery.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div>
        <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">Sistema</p>
        <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
          Configurações
        </h1>
        <p className="text-ink-500 mt-1 text-sm">
          Campanhas globais e preços de restart — sem precisar de deploy.
        </p>
      </div>

      {toggle.error && (
        <Alert tone="danger" title="Falhou">
          {toggle.error instanceof PnlApiError ? toggle.error.api.message : 'Erro ao salvar'}
        </Alert>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Megaphone size={16} className="text-brand-500" />
            <CardTitle>Campanhas</CardTitle>
          </div>
        </CardHeader>
        <CardBody>
          {campaignsQuery.isLoading ? (
            <div className="flex justify-center py-10">
              <Spinner />
            </div>
          ) : (
            <ul className="divide-ink-100 divide-y">
              {items.map((c) => (
                <li
                  key={c.key}
                  className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-ink-900 text-sm font-semibold">{c.label}</p>
                      <Badge tone={c.enabled ? 'success' : 'neutral'}>
                        {c.enabled ? 'Ligada' : 'Desligada'}
                      </Badge>
                    </div>
                    <p className="text-ink-500 mt-1 text-xs">{c.description}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={c.enabled ? 'secondary' : 'primary'}
                    leadingIcon={c.enabled ? <Power size={13} /> : <Check size={13} />}
                    loading={toggle.isPending && toggle.variables?.key === c.key}
                    onClick={() => toggle.mutate({ key: c.key, enabled: !c.enabled })}
                  >
                    {c.enabled ? 'Desligar' : 'Ligar'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <RestartSettingsCard />
      <CheckoutFeesCard />
      <NewTestBannerCard />
      <ExcellenceCard />
      <BannersCard />
      <OperatingWindowsCard />
      <TestEmailsCard />
    </div>
  );
}

const BANNER_INPUT =
  'border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none';

function BannerFields({
  title,
  hint,
  cfg,
  onChange,
}: {
  title: string;
  hint: string;
  cfg: BannerConfig;
  onChange: (next: BannerConfig) => void;
}) {
  return (
    <div className="border-ink-100 space-y-3 border-t pt-5 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-ink-900 text-sm font-semibold">{title}</p>
            <Badge tone={cfg.enabled ? 'success' : 'neutral'}>
              {cfg.enabled ? 'Ligado' : 'Desligado'}
            </Badge>
          </div>
          <p className="text-ink-500 mt-1 text-xs">{hint}</p>
        </div>
        <Button
          size="sm"
          variant={cfg.enabled ? 'secondary' : 'primary'}
          leadingIcon={cfg.enabled ? <Power size={13} /> : <Check size={13} />}
          onClick={() => onChange({ ...cfg, enabled: !cfg.enabled })}
        >
          {cfg.enabled ? 'Desligar' : 'Ligar'}
        </Button>
      </div>
      <div>
        <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">Texto</label>
        <input
          value={cfg.text}
          onChange={(e) => onChange({ ...cfg, text: e.target.value })}
          placeholder="Ex.: Oferta exclusiva: 20% de desconto até sexta-feira!"
          className={BANNER_INPUT}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
            Texto do botão
          </label>
          <input
            value={cfg.buttonText}
            onChange={(e) => onChange({ ...cfg, buttonText: e.target.value })}
            placeholder="Pegar o Desconto"
            className={BANNER_INPUT}
          />
        </div>
        <div>
          <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
            URL do botão
          </label>
          <input
            type="url"
            inputMode="url"
            value={cfg.buttonUrl ?? ''}
            onChange={(e) => onChange({ ...cfg, buttonUrl: e.target.value })}
            placeholder="https://..."
            className={BANNER_INPUT}
          />
        </div>
      </div>
    </div>
  );
}

function BannersCard() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'settings', 'banners'],
    queryFn: () => api.getBannerSettings(),
  });

  const empty: BannerConfig = {
    enabled: false,
    text: '',
    buttonText: 'Pegar o Desconto',
    buttonUrl: null,
  };
  const [top, setTop] = useState<BannerConfig>(empty);
  const [promo, setPromo] = useState<BannerConfig>(empty);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (query.data && !initialized) {
      setTop(query.data.top);
      setPromo(query.data.promo);
      setInitialized(true);
    }
  }, [query.data, initialized]);

  const save = useMutation({
    mutationFn: () =>
      api.setBannerSettings({
        top: { ...top, buttonUrl: top.buttonUrl?.trim() || null },
        promo: { ...promo, buttonUrl: promo.buttonUrl?.trim() || null },
      }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'banners'], data);
      setTop(data.top);
      setPromo(data.promo);
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Megaphone size={16} className="text-brand-500" />
          <CardTitle>Banners promocionais</CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-ink-500 mb-4 text-xs leading-relaxed">
          Dois banners azuis pra campanhas, controlados aqui (liga/desliga + texto editável). A{' '}
          <strong className="text-ink-700">barra do topo</strong> aparece no alto da plataforma e o
          trader pode fechar; o <strong className="text-ink-700">banner do dashboard</strong>{' '}
          aparece no topo do painel. Deixe a URL vazia pra um banner só de aviso (sem botão).
        </p>
        {query.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-5">
            <BannerFields
              title="Barra do topo (dismissível)"
              hint="Faixa fina no topo de toda a plataforma, com botão de fechar."
              cfg={top}
              onChange={setTop}
            />
            <BannerFields
              title="Banner do dashboard"
              hint="Banner maior no topo do dashboard do trader (onde aparece o aviso de restart)."
              cfg={promo}
              onChange={setPromo}
            />
            {save.error && (
              <Alert tone="danger" title="Falhou">
                {save.error instanceof PnlApiError ? save.error.api.message : 'Erro ao salvar'}
              </Alert>
            )}
            <div className="flex justify-end">
              <Button
                leadingIcon={<Save size={14} />}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Salvar
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

const WEEKDAYS = [
  { idx: 1, label: 'Seg' },
  { idx: 2, label: 'Ter' },
  { idx: 3, label: 'Qua' },
  { idx: 4, label: 'Qui' },
  { idx: 5, label: 'Sex' },
  { idx: 6, label: 'Sáb' },
  { idx: 0, label: 'Dom' },
];

/** Janelas de operação (M5): horário permitido por mercado (B3 / Forex). */
function OperatingWindowsCard() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'settings', 'operating-windows'],
    queryFn: () => api.getOperatingWindows(),
  });

  const [b3, setB3] = useState<OperatingWindow | null>(null);
  const [forex, setForex] = useState<OperatingWindow | null>(null);

  useEffect(() => {
    if (query.data && !b3 && !forex) {
      setB3(query.data.b3);
      setForex(query.data.forex);
    }
  }, [query.data, b3, forex]);

  const save = useMutation({
    mutationFn: () => api.setOperatingWindows({ b3: b3!, forex: forex! }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'operating-windows'], data);
      setB3(data.b3);
      setForex(data.forex);
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-brand-500" />
          <CardTitle>Janelas de operação</CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-ink-500 mb-4 text-xs leading-relaxed">
          Horário permitido de operação por mercado. Trades fora da janela são{' '}
          <strong className="text-ink-700">sinalizados</strong> na importação do CSV (não bloqueiam
          nada hoje — política de bloqueio/penalização é configurável depois).
        </p>
        {query.isLoading || !b3 || !forex ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-6">
            <WindowEditor title="B3 (Profit)" window={b3} onChange={setB3} />
            <WindowEditor title="Forex (BlackArrow)" window={forex} onChange={setForex} />
            {save.error && (
              <Alert tone="danger" title="Falhou">
                {save.error instanceof PnlApiError ? save.error.api.message : 'Erro ao salvar'}
              </Alert>
            )}
            <div className="flex justify-end">
              <Button
                leadingIcon={<Save size={14} />}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Salvar janelas
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function WindowEditor({
  title,
  window: win,
  onChange,
}: {
  title: string;
  window: OperatingWindow;
  onChange: (w: OperatingWindow) => void;
}) {
  function toggleDay(idx: number) {
    const has = win.weekdays.includes(idx);
    const weekdays = has
      ? win.weekdays.filter((d) => d !== idx)
      : [...win.weekdays, idx].sort((a, b) => a - b);
    onChange({ ...win, weekdays });
  }
  return (
    <div className="border-ink-100 rounded-lg border p-4">
      <p className="text-ink-900 mb-3 text-sm font-semibold">{title}</p>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {WEEKDAYS.map((d) => {
          const on = win.weekdays.includes(d.idx);
          return (
            <button
              key={d.idx}
              type="button"
              onClick={() => toggleDay(d.idx)}
              className={
                'rounded-md px-2.5 py-1 text-xs font-medium transition-colors ' +
                (on ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-500 hover:bg-ink-200')
              }
            >
              {d.label}
            </button>
          );
        })}
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
            Abre
          </label>
          <input
            type="time"
            value={win.open}
            onChange={(e) => onChange({ ...win, open: e.target.value })}
            className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-2 py-1.5 text-sm focus:outline-none"
          />
        </div>
        <div>
          <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
            Fecha
          </label>
          <input
            type="time"
            value={win.close}
            onChange={(e) => onChange({ ...win, close: e.target.value })}
            className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-2 py-1.5 text-sm focus:outline-none"
          />
        </div>
        <div>
          <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
            Fuso
          </label>
          <input
            type="text"
            value={win.timezone}
            onChange={(e) => onChange({ ...win, timezone: e.target.value })}
            placeholder="America/Sao_Paulo"
            className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-2 py-1.5 text-xs focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
}

/** Dispara todos os templates de email para um endereço, pra validar visual/texto. */
function TestEmailsCard() {
  const [to, setTo] = useState('');

  const send = useMutation({
    mutationFn: () => api.sendTestEmails({ to: to.trim() }),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Mail size={16} className="text-brand-500" />
          <CardTitle>Emails de teste</CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-ink-500 mb-4 text-xs leading-relaxed">
          Dispara <strong className="text-ink-700">todos</strong> os templates (boas-vindas,
          aprovado, reprovado+restart, restart falho+cupom, sequência de plataforma, contrato,
          pagamento…) com dados de exemplo para o email abaixo. Cada assunto vem com o prefixo{' '}
          <span className="font-mono">[TESTE]</span>.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
              Enviar para
            </label>
            <input
              type="email"
              inputMode="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="voce@exemplo.com"
              className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none"
            />
          </div>
          <Button
            leadingIcon={<Send size={14} />}
            loading={send.isPending}
            disabled={!to.trim()}
            onClick={() => send.mutate()}
          >
            Enviar testes
          </Button>
        </div>
        {send.isSuccess && (
          <Alert tone="success" title="Enviado">
            {send.data.sent} de {send.data.requested} emails de teste foram enviados para {to}.
          </Alert>
        )}
        {send.error && (
          <Alert tone="danger" title="Falhou">
            {send.error instanceof PnlApiError ? send.error.api.message : 'Erro ao enviar'}
          </Alert>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Botão "Excelência Trading" no dashboard do trader: liga/desliga + URL externa.
 * Quando ligado e com URL, aparece pra todos os traders no topo do dashboard.
 */
function ExcellenceCard() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'settings', 'excellence'],
    queryFn: () => api.getExcellenceSettings(),
  });

  const [enabled, setEnabled] = useState(false);
  const [url, setUrl] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (query.data && !initialized) {
      setEnabled(query.data.enabled);
      setUrl(query.data.url ?? '');
      setInitialized(true);
    }
  }, [query.data, initialized]);

  const save = useMutation({
    mutationFn: () => api.setExcellenceSettings({ enabled, url: url.trim() || null }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'excellence'], data);
      setEnabled(data.enabled);
      setUrl(data.url ?? '');
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Award size={16} className="text-brand-500" />
          <CardTitle>Excelência Trading</CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-ink-500 mb-4 text-xs leading-relaxed">
          Quando ligado (com uma URL), aparece um botão azul “Excelência Trading” no topo do
          dashboard de <strong className="text-ink-700">todos</strong> os traders, que abre o link
          em uma nova aba.
        </p>
        {query.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-ink-900 text-sm font-semibold">Mostrar o botão</p>
                  <Badge tone={enabled ? 'success' : 'neutral'}>
                    {enabled ? 'Ligado' : 'Desligado'}
                  </Badge>
                </div>
                <p className="text-ink-500 mt-1 text-xs">
                  Liga/desliga o botão pra todos os traders.
                </p>
              </div>
              <Button
                size="sm"
                variant={enabled ? 'secondary' : 'primary'}
                leadingIcon={enabled ? <Power size={13} /> : <Check size={13} />}
                onClick={() => setEnabled((v) => !v)}
              >
                {enabled ? 'Desligar' : 'Ligar'}
              </Button>
            </div>
            <div>
              <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                URL do botão
              </label>
              <input
                type="url"
                inputMode="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none"
              />
            </div>
            {save.error && (
              <Alert tone="danger" title="Falhou">
                {save.error instanceof PnlApiError ? save.error.api.message : 'Erro ao salvar'}
              </Alert>
            )}
            <div className="flex justify-end">
              <Button
                leadingIcon={<Save size={14} />}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Salvar
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Banner de "novo teste" (após a 2ª eliminação): texto editável + cupom aplicado.
 * O placeholder [CUPOM] é trocado pelo código do cupom no dashboard do trader.
 */
function NewTestBannerCard() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'settings', 'new-test-banner'],
    queryFn: () => api.getNewTestBannerSettings(),
  });

  const [text, setText] = useState('');
  const [coupon, setCoupon] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (query.data && !initialized) {
      setText(query.data.text);
      setCoupon(query.data.couponCode ?? '');
      setInitialized(true);
    }
  }, [query.data, initialized]);

  const save = useMutation({
    mutationFn: () =>
      api.setNewTestBannerSettings({ text: text.trim(), couponCode: coupon.trim() || null }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'new-test-banner'], data);
      setText(data.text);
      setCoupon(data.couponCode ?? '');
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Megaphone size={16} className="text-brand-500" />
          <CardTitle>Banner de novo teste</CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-ink-500 mb-4 text-xs leading-relaxed">
          Mensagem que aparece no dashboard quando o trader estoura a conta pela 2ª vez (oferta de
          um novo Challenge). Use <code className="text-ink-700">[CUPOM]</code> onde o código do
          cupom deve aparecer.
        </p>
        {query.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                Texto do banner
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                maxLength={400}
                placeholder="Até os melhores traders tomam stop. Use o cupom [CUPOM]…"
                className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                Cupom aplicado no link
              </label>
              <input
                type="text"
                value={coupon}
                onChange={(e) => setCoupon(e.target.value)}
                placeholder="Ex.: VOLTA20 (deixe vazio pra sem cupom)"
                className="border-ink-200 text-ink-900 focus:border-brand-500 mt-1 w-full rounded-md border bg-white px-3 py-2 text-sm uppercase focus:outline-none"
              />
            </div>
            {save.error && (
              <Alert tone="danger" title="Falhou">
                {save.error instanceof PnlApiError ? save.error.api.message : 'Erro ao salvar'}
              </Alert>
            )}
            <div className="flex justify-end">
              <Button
                leadingIcon={<Save size={14} />}
                loading={save.isPending}
                disabled={text.trim().length === 0}
                onClick={() => save.mutate()}
              >
                Salvar
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function centsToInput(cents: number | null): string {
  return cents === null ? '' : (cents / 100).toFixed(2);
}

function inputToCents(value: string): number | null {
  const t = value.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100);
}

function currencySymbol(c: 'BRL' | 'USD'): string {
  return c === 'USD' ? 'US$' : 'R$';
}

/**
 * Restart — documentação dos 2 modos + preços por plano (controle total no admin).
 * - Modo incluso (campanha / liberação manual por conta): refaz grátis.
 * - Modo pago (padrão): checkout interno; ao pagar, a conta reinicia sozinha.
 */
function RestartSettingsCard() {
  const qc = useQueryClient();

  const pricesQuery = useQuery({
    queryKey: ['admin', 'settings', 'restart-prices'],
    queryFn: () => api.getRestartPrices(),
  });

  const [values, setValues] = useState<Record<string, string>>({});
  const [generic, setGeneric] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (pricesQuery.data && !initialized) {
      const v: Record<string, string> = {};
      for (const it of pricesQuery.data.items) v[it.code] = centsToInput(it.priceCents);
      setValues(v);
      setGeneric(centsToInput(pricesQuery.data.genericPriceCents));
      setInitialized(true);
    }
  }, [pricesQuery.data, initialized]);

  const save = useMutation({
    mutationFn: () =>
      api.setRestartPrices({
        items: (pricesQuery.data?.items ?? []).map((it) => ({
          code: it.code,
          priceCents: inputToCents(values[it.code] ?? ''),
        })),
        genericPriceCents: inputToCents(generic),
      }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'restart-prices'], data);
      const v: Record<string, string> = {};
      for (const it of data.items) v[it.code] = centsToInput(it.priceCents);
      setValues(v);
      setGeneric(centsToInput(data.genericPriceCents));
    },
  });

  const items = pricesQuery.data?.items ?? [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <RotateCcw size={16} className="text-brand-500" />
          <CardTitle>Restart — preços e funcionamento</CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <div className="border-ink-200 bg-ink-50 text-ink-600 mb-5 space-y-2 rounded-lg border p-4 text-xs leading-relaxed">
          <p className="text-ink-900 text-sm font-semibold">Como o restart funciona (2 modos)</p>
          <p>
            <strong className="text-ink-900">1. Restart incluso (campanha):</strong> quando a
            campanha “Restart incluso” está ligada (acima) ou você libera manualmente em{' '}
            <em>Contas → “Liberar restart incluso”</em>, o trader reprovado vê no painel e em
            “Minhas contas” que tem um restart grátis e refaz a avaliação <strong>sem pagar</strong>
            .
          </p>
          <p>
            <strong className="text-ink-900">2. Restart pago (padrão):</strong> sem restart incluso,
            o trader reprovado vê o botão de restart que abre o{' '}
            <strong>nosso checkout interno</strong>; ao pagar, a conta{' '}
            <strong>reinicia automaticamente</strong>. Os valores por plano são definidos abaixo.
          </p>
          <p className="text-ink-500">
            Sem preço configurado, o trader vê “Restart em breve” e nada é cobrado. O valor genérico
            é usado quando o plano não tem preço próprio.
          </p>
        </div>

        {pricesQuery.isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.code} className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-ink-900 text-sm font-medium">{it.label}</p>
                  <p className="text-ink-400 text-2xs uppercase tracking-wide">
                    {it.code} · {it.currency}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-ink-500 text-sm">{currencySymbol(it.currency)}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={values[it.code] ?? ''}
                    onChange={(e) => setValues((s) => ({ ...s, [it.code]: e.target.value }))}
                    placeholder="—"
                    className="border-ink-200 text-ink-900 focus:border-brand-500 w-28 rounded-md border bg-white px-2.5 py-1.5 text-right text-sm focus:outline-none"
                  />
                </div>
              </div>
            ))}

            <div className="border-ink-100 mt-2 flex items-center justify-between gap-4 border-t pt-3">
              <div className="min-w-0">
                <p className="text-ink-900 text-sm font-medium">Valor genérico (fallback)</p>
                <p className="text-ink-400 text-2xs uppercase tracking-wide">restart.price_cents</p>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-ink-500 text-sm">R$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={generic}
                  onChange={(e) => setGeneric(e.target.value)}
                  placeholder="—"
                  className="border-ink-200 text-ink-900 focus:border-brand-500 w-28 rounded-md border bg-white px-2.5 py-1.5 text-right text-sm focus:outline-none"
                />
              </div>
            </div>

            {save.error && (
              <Alert tone="danger" title="Falhou">
                {save.error instanceof PnlApiError ? save.error.api.message : 'Erro ao salvar'}
              </Alert>
            )}

            <div className="flex items-center justify-between pt-2">
              <p className="text-ink-400 text-xs">Deixe em branco para limpar (cai no genérico).</p>
              <Button
                leadingIcon={<Save size={14} />}
                loading={save.isPending}
                onClick={() => save.mutate()}
              >
                Salvar preços
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function fmtBrl(cents: number): string {
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Converte "4,69" ou "4.69" em % (0-30, casas decimais ok); null = inválido. */
function parseFeePercent(v: string): number | null {
  const t = v.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 && n <= 30 ? n : null;
}

/** Converte "0,39" (R$) em cents inteiros (0 a R$100); null = inválido. */
function parseFeeFixedCents(v: string): number | null {
  const t = v.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) return null;
  const cents = Math.round(n * 100);
  return cents <= 10_000 ? cents : null;
}

/**
 * Taxas do cartão repassadas por parcela no 12x (gross-up do checkout).
 * Mesma conta do grossUpInstallments usado pela API (Stripe Price dinâmico)
 * e pelos fronts (cards/resumo) — mudar aqui muda o 12x pra vendas NOVAS.
 */
function CheckoutFeesCard() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['admin', 'settings', 'checkout-fees'],
    queryFn: () => api.getCheckoutFeeSettings(),
  });

  const [percent, setPercent] = useState('');
  const [fixed, setFixed] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (query.data && !initialized) {
      setPercent(String(query.data.cardFeePercent));
      setFixed((query.data.cardFeeFixedCents / 100).toFixed(2));
      setInitialized(true);
    }
  }, [query.data, initialized]);

  const percentParsed = parseFeePercent(percent);
  const fixedParsed = parseFeeFixedCents(fixed);
  const valid = percentParsed !== null && fixedParsed !== null;

  const save = useMutation({
    mutationFn: () =>
      api.setCheckoutFeeSettings({
        cardFeePercent: percentParsed!,
        cardFeeFixedCents: fixedParsed!,
      }),
    onSuccess: (data) => {
      qc.setQueryData(['admin', 'settings', 'checkout-fees'], data);
      setPercent(String(data.cardFeePercent));
      setFixed((data.cardFeeFixedCents / 100).toFixed(2));
    },
  });

  // Preview com um plano de exemplo — mesma função pura usada pela API.
  const exampleCents = 100_000;
  const preview = valid
    ? grossUpInstallments(exampleCents, {
        feePercent: percentParsed,
        feeFixedCents: fixedParsed,
      })
    : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-brand-500" />
          <CardTitle>Checkout — taxas do cartão ({CARD_INSTALLMENTS}x)</CardTitle>
        </div>
      </CardHeader>
      <CardBody>
        <p className="text-ink-500 mb-4 text-xs leading-relaxed">
          Repasse das taxas embutido em <strong className="text-ink-700">cada parcela</strong> do{' '}
          {CARD_INSTALLMENTS}x no cartão (gross-up sobre o valor líquido, adicionais e cupom
          inclusos). O padrão cobre o custo Stripe no Brasil:{' '}
          <strong className="text-ink-700">3,99% + R$0,39</strong> por cobrança +{' '}
          <strong className="text-ink-700">0,7%</strong> do Billing = 4,69% + R$0,39. Vale só pra
          vendas novas; o Pix à vista não usa essas taxas.
        </p>
        {query.isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                  % por parcela
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={percent}
                  onChange={(e) => setPercent(e.target.value)}
                  placeholder="4,69"
                  className={BANNER_INPUT}
                />
              </div>
              <div>
                <label className="text-2xs text-ink-500 font-semibold uppercase tracking-wide">
                  Taxa fixa por parcela (R$)
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={fixed}
                  onChange={(e) => setFixed(e.target.value)}
                  placeholder="0,39"
                  className={BANNER_INPUT}
                />
              </div>
            </div>
            {!valid && (
              <p className="text-danger-600 text-xs">
                Valores inválidos — use % entre 0 e 30 (ex.: 4,69) e taxa fixa entre R$0,00 e
                R$100,00.
              </p>
            )}
            {preview && (
              <p className="text-ink-500 text-xs tabular-nums">
                Exemplo: plano de {fmtBrl(exampleCents)} → {CARD_INSTALLMENTS}x de{' '}
                <strong className="text-ink-700">{fmtBrl(preview.installmentCents)}</strong> (total
                no cartão {fmtBrl(preview.totalCents)}).
              </p>
            )}
            {save.error && (
              <Alert tone="danger" title="Falhou">
                {save.error instanceof PnlApiError ? save.error.api.message : 'Erro ao salvar'}
              </Alert>
            )}
            <div className="flex justify-end">
              <Button
                leadingIcon={<Save size={14} />}
                loading={save.isPending}
                disabled={!valid}
                onClick={() => save.mutate()}
              >
                Salvar taxas
              </Button>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
